import { useMutation, useQueryClient } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { allMonthCalendarQueries } from '@opallabs/api/BFF'
import { content as contentV2 } from '@opallabs/api/v2'
import { momentSchedules, moments } from '@opallabs/api/v3'
import { toast, Toast } from '@opallabs/lapidary'
import { CreatedToast } from './CreatedToast'

export type CreateCalendarItemParams = {
  date: Date
  title: string
} & (
  | {
      kind: 'moment'
      /** The uuid of a moment stamp to build the new moment from. */
      stampId?: string
    }
  | {
      kind: 'content'
      /** The numeric id of the channel the content publishes to. */
      serviceId: string
      /** The numeric id of the account it publishes from, when one was chosen. */
      accountId?: string
      /** The uuid of a post stamp to build the new content from. */
      stampId?: string
    }
)

/**
 * Makes a moment on a day, from a stamp when one was chosen.
 *
 * The schedule is a resource of its own, so a dated moment takes two calls: the
 * moment cannot be created with a date on it.
 */
const createMomentOnDay = async ({
  date,
  stampId,
  storyId,
  title,
  workspaceUuid,
}: {
  date: Date
  stampId?: string
  storyId?: string
  title: string
  workspaceUuid: string
}) => {
  const moment = await moments.create({
    requestBody: {
      data: {
        attributes: { stamp_id: stampId, title },
        relationships: {
          stories:
            storyId === undefined
              ? undefined
              : { data: [{ id: storyId, type: 'story' }] },
          workspace: { data: { id: workspaceUuid, type: 'workspace' } },
        },
        type: 'moment',
      },
    },
  })

  await momentSchedules.create({
    requestBody: {
      data: {
        attributes: {
          start_at: formatISO(date, { representation: 'date' }),
        },
        relationships: {
          moment: { data: { id: moment.data.id, type: 'moment' } },
        },
        type: 'moment_schedule',
      },
    },
  })

  return moment
}

/**
 * Makes something on a day of the calendar.
 *
 * Content always arrives inside a moment, because that is the only way the API
 * can make one, so asking for content on an empty day makes the moment to hold
 * it. That is what the week view's own quick create does today.
 *
 * The account is set after the fact: `POST /content/v2` takes a channel but no
 * account, and only the follow-up `PATCH` can name one. Beware that naming an
 * account applies that account's own default stamp, which overwrites content
 * built from the stamp the create call named.
 */
export const useCreateCalendarItem = ({
  getClearFilters,
  storyId,
  workspaceUuid,
}: {
  /**
   * Answers with a way to clear the applied filters when some are on, and
   * `null` when none are. Filters can hide what was just made, so the success
   * toast offers the way out.
   */
  getClearFilters?: () => (() => void) | null
  /** Puts everything made here into the story the calendar is scoped to. */
  storyId?: string
  workspaceUuid: string
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateCalendarItemParams) => {
      const moment = await createMomentOnDay({
        date: params.date,
        stampId: params.kind === 'moment' ? params.stampId : undefined,
        storyId,
        title: params.title,
        workspaceUuid,
      })

      if (params.kind === 'moment') return

      const created = await contentV2.create({
        requestBody: {
          data: {
            attributes: { name: params.title, stamp_id: params.stampId },
            relationships: {
              moment: {
                data: {
                  id: moment.data.attributes.legacy_id,
                  type: 'moment',
                },
              },
              service: { data: { id: params.serviceId, type: 'service' } },
            },
            type: 'content',
          },
        },
      })

      if (params.accountId === undefined) return

      await contentV2.update({
        params: { path: { content_id: created.data.id } },
        requestBody: {
          data: {
            attributes: {},
            id: created.data.id,
            relationships: {
              account: { data: { id: params.accountId, type: 'account' } },
            },
            type: 'content',
          },
        },
      })
    },

    onError: () => {
      toast(
        (toastProps) => (
          <Toast
            {...toastProps}
            message="That could not be created. Please try again."
            title="Create failed"
          />
        ),
        { type: 'error' }
      )
    },

    // A create that fails partway can leave a moment behind, and the calendar
    // has to show it.
    onSettled: () => {
      allMonthCalendarQueries.invalidateCache(queryClient, { workspaceUuid })
    },

    onSuccess: (_result, params) => {
      const clearFilters = getClearFilters?.() ?? null
      toast(
        (toastProps) => (
          <CreatedToast
            {...toastProps}
            title={params.title}
            onClearFilters={clearFilters ?? undefined}
          />
        ),
        { type: 'success' }
      )
    },
  })
}
